import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './Dashboard';
import UserView from './components/UserView';
import SuperAdminDashboard from './components/superadmin/SuperAdminDashboard';

// Function to check if user is authenticated via token
const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  
  try {
    // Check if token is expired
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiration = payload.exp * 1000; // Convert to milliseconds
    return Date.now() < expiration;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

// Function to get user info from token
const getUserInfo = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Extract name from email
    const email = payload.sub;
    const usernameFromEmail = email.split('@')[0];
    const capitalizedName = usernameFromEmail.charAt(0).toUpperCase() + usernameFromEmail.slice(1);
    
    return {
      email: payload.sub,
      user_id: payload.user_id,
      role: payload.role,
      name: capitalizedName, // Add the name
      token: token
    };
  } catch (error) {
    console.error('Error getting user info:', error);
    return null;
  }
};


function App() {
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const [userInfo, setUserInfo] = useState(getUserInfo());

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      setIsAuth(authenticated);
      if (authenticated) {
        setUserInfo(getUserInfo());
      }
    };
    
    checkAuth();
    
    // Listen for storage changes (logout from other tabs)
    const handleStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Protected route wrapper component
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!isAuth) {
      return <Navigate to="/login" replace />;
    }
    
    if (allowedRoles && !allowedRoles.includes(userInfo?.role)) {
      // Redirect based on role
      if (userInfo?.role === 'superadmin') {
        return <Navigate to="/superadmin" replace />;
      } else if (userInfo?.role === 'admin') {
        return <Navigate to="/dashboard" replace />;
      } else {
        return <Navigate to="/user-view" replace />;
      }
    }
    
    return children;
  };

  return (
    <Router>
      
      <Routes>
        <Route 
          path="/login" 
          element={
            isAuth ? 
            <Navigate to={
              userInfo?.role === 'superadmin' ? "/superadmin" : 
              userInfo?.role === 'admin' ? "/dashboard" : 
              "/user-view"
            } /> : 
            <Login 
              setIsAuthenticated={setIsAuth}
              setUserInfo={setUserInfo}
            />
          } 
        />
        
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
              <Dashboard userInfo={userInfo} setIsAuthenticated={setIsAuth} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/user-view" 
          element={
            <ProtectedRoute allowedRoles={['user']}>
              <UserView userInfo={userInfo} setIsAuthenticated={setIsAuth} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/superadmin" 
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <SuperAdminDashboard userInfo={userInfo} setIsAuthenticated={setIsAuth} />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/" element={<Navigate to="/login" />} />
        
        {/* Fallback route for unauthorized access */}
        <Route path="*" element={
          isAuth ? 
          <Navigate to={
            userInfo?.role === 'superadmin' ? "/superadmin" : 
            userInfo?.role === 'admin' ? "/dashboard" : 
            "/user-view"
          } /> : 
          <Navigate to="/login" />
        } />
      </Routes>
    </Router>
  );
}

export default App;